import moment from "moment";
import ApexCharts from "apexcharts";
import { useLazyQuery } from "@apollo/client";
import LoadingPage from "../Include/LoadingPage";
import { useState, useEffect, useRef } from "react";
// import { currencyFormat } from '../../Function/DynamicFn'
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Box, Typography, Stack, TextField, Grid } from "@mui/material";
import { GET_DASHBOARD_STOCK_INFO } from "../../Schema/Dashboard";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

export default function DashboardStockChart() {
  const [results, setResults] = useState([]);
  const [selectYear, setSelectYear] = useState(moment().format("YYYY"));
  const [chart, setChart] = useState(null);
  const [chart2, setChart2] = useState(null);
  const chartRef = useRef(null);
  const chartRef2 = useRef(null);

  // Move options inside component to access results
  const getChartOptions = (showValue) => ({
    series:
      showValue === "amount"
        ? [
            {
              name: "តម្លៃចូលស្តុក",
              type: "line",
              data: results?.map(
                (r) => parseFloat(r?.totalStockInAmount?.toFixed(2)) || 0,
              ),
            },
            {
              name: "តម្លៃចេញស្តុក",
              type: "line",
              data: results?.map(
                (r) => parseFloat(r?.totalStockOutAmount?.toFixed(2)) || 0,
              ),
            },
            {
              name: "តម្លៃនៅសល់",
              type: "line",
              data: results?.map(
                (r) => parseFloat(r?.totalRemainInStockAmount?.toFixed(2)) || 0,
              ),
            },
          ]
        : [
            {
              name: "ចំនួនចូលស្តុក",
              type: "column",
              data: results?.map(
                (r) => parseFloat(r?.totalStockInQty?.toFixed(2)) || 0,
              ),
            },
            {
              name: "ចំនួនចេញស្តុក",
              type: "column",
              data: results?.map(
                (r) => parseFloat(r?.totalStockOutQty?.toFixed(2)) || 0,
              ),
            },
            {
              name: "ចំនួននៅសល់",
              type: "column",
              data: results?.map(
                (r) => parseFloat(r?.totalRemainInStockQty?.toFixed(2)) || 0,
              ),
            },
          ],
    colors: ["#26a0fc", "#00e396", "#feb019"],
    chart: {
      // height: 350,
      // type: 'line',
      // stacked: false,
      fontFamily: "KhmerOsSiemReap",
    },
    plotOptions: {
      bar: {
        dataLabels: {
          position: "top", // top of bar
        },
      },
    },
    stroke: {
      curve: "smooth",
    },
    // dataLabels: {
    //    enabled: true,
    //    formatter: function (val) {
    //       return showValue === 'amount' ? '$ ' + val.toLocaleString() : val.toLocaleString()
    //    },
    //    offsetY: -10,
    //    // style: {
    //    //    fontSize: '12px',
    //    // }
    // },
    yaxis: {
      labels: {
        formatter: function (val) {
          return showValue === "amount"
            ? "$ " + val.toLocaleString()
            : val.toLocaleString();
        },
      },
    },
    stroke: {
      width: showValue === "amount" ? [2, 2, 2] : [1, 1, 1],
      curve: ["monotoneCubic", "monotoneCubic", "monotoneCubic"],
    },
    xaxis: {
      categories: [
        "មករា",
        "កុម្ភៈ",
        "មិនា",
        "មេសា",
        "ឧសភា",
        "មិថុនា",
        "កក្កដា",
        "សីហា",
        "កញ្ញា",
        "តុលា",
        "វិច្ឆិកា",
        "ធ្នូ",
      ],
    },
    tooltip: {
      fixed: {
        enabled: true,
        position: "topLeft", // topRight, topLeft, bottomRight, bottomLeft
        offsetY: 30,
        offsetX: 60,
      },
    },
    legend: {
      horizontalAlign: "left",
      offsetX: 40,
    },
  });

  const [getDashboardInfo] = useLazyQuery(GET_DASHBOARD_STOCK_INFO, {
    onCompleted: ({ getDashboardStockInfo }) => {
      setResults((prev) => [...prev, getDashboardStockInfo]);
    },
    onError: (err) => {
      console.log("Error: getDashboardInfo", err?.message);
    },
  });

  const runMultipleQueries = async () => {
    const dates = [];
    for (let month = 0; month < 12; month++) {
      const start = moment(selectYear, "YYYY").month(month).startOf("month");
      const end = moment(selectYear, "YYYY").month(month).endOf("month");
      dates.push({
        from: start.format("YYYY-MM-DD"),
        to: end.format("YYYY-MM-DD"),
      });
    }

    for (const { from, to } of dates) {
      await getDashboardInfo({
        variables: { from, to },
      });
    }
  };

  // ✅ Run automatically on page load
  // console.log({ results })

  // | 'line'
  // | 'area'
  // | 'bar'
  // | 'pie'
  // | 'donut'
  // | 'radialBar'
  // | 'scatter'
  // | 'bubble'
  // | 'heatmap'
  // | 'candlestick'
  // | 'boxPlot'
  // | 'radar'
  // | 'polarArea'
  // | 'rangeBar'
  // | 'rangeArea'
  // | 'treemap'

  useEffect(() => {
    setResults([]);
    runMultipleQueries();
  }, [selectYear]);

  useEffect(() => {
    if (results.length === 12) {
      // Wait for all 12 months
      if (chart) chart.destroy();
      if (chart2) chart2.destroy();

      const newChart = new ApexCharts(
        chartRef.current,
        getChartOptions("amount"),
      );
      setChart(newChart);
      newChart.render();

      // ✅ Chart 2 (different data/options)
      const newChart2 = new ApexCharts(
        chartRef2.current,
        getChartOptions("qty"),
      );
      setChart2(newChart2);
      newChart2.render();
    }
    return () => {
      if (chart) chart.destroy();
      if (chart2) chart2.destroy();
    };
  }, [results]);

  return (
    <Stack
      className=""
      sx={{
        padding: "20px",
        bgcolor: "white",
        borderRadius: "12px",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={2}>
        <Typography fontWeight="bold" className="title">
          ក្រាបតម្លៃនឹងចំនួនទិញចូល​​​និងដកចេញប្រចាំខែ(មករា-ធ្នូ) ឆ្នាំ
        </Typography>
        <Box sx={{ alignItems: "end" }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              value={new Date(selectYear)}
              views={["year"]}
              onChange={(e) => setSelectYear(moment(e).format("YYYY"))}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                },
              }}
              renderInput={(params) => (
                <TextField {...params} size="small" fullWidth />
              )}
            />
          </LocalizationProvider>
        </Box>
      </Stack>
      <Grid container spacing={2} mt={1}>
        <Grid item xs={6}>
          {results.length < 12 ? (
            <LoadingPage height={"400px"} />
          ) : (
            <div ref={chartRef}></div>
          )}
        </Grid>
        <Grid item xs={6}>
          {results.length < 12 ? (
            <LoadingPage height={"400px"} />
          ) : (
            <div ref={chartRef2}></div>
          )}
        </Grid>
      </Grid>
    </Stack>
  );
}
